import { FC, useCallback, useState } from 'react';
import { Theme, Tooltip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AppOwnerRole } from 'common-types';
import OwnersEditModalContainer from '../../containers/OwnersEditModalContainer';
import OwnerSection from './OwnerSection';
import OwnersList from './OwnersList';
import OwnersTooltip from './OwnersTooltip';
import { ApplicationInfo, Owner } from '../../applications-types';
import { updateAppOwnerEditRequest } from '../../../application-owners/api/applications-owners-api';

const Personas: FC<React.PropsWithChildren<{ app: ApplicationInfo }>> = ({
  app,
}) => {
  const { classes } = useStyles();
  const [showEditAppOwnerDialog, setShowEditAppOwnerDialog] = useState(false);
  const onEdit = useCallback(() => {
    setShowEditAppOwnerDialog(true);
  }, []);

  return (
    <div className={classes.personas}>
      <OwnersEditModalContainer
        setOpen={setShowEditAppOwnerDialog}
        open={showEditAppOwnerDialog}
      />
      <OwnerSection title='Creator'>
        <Typography
          variant='body1'
          color='text.primary'
          sx={{ textTransform: 'capitalize' }}>
          {app.creator}
        </Typography>
      </OwnerSection>
      <OwnerSection
        onEdit={() => {
          onEdit();
          updateAppOwnerEditRequest('role', AppOwnerRole.Security);
        }}
        title='Security Owners'
        appId={app.appId}
        editable={resolveEditable(app.owners[AppOwnerRole.Security])}>
        <OwnersList
          onAddOwners={onEdit}
          owners={app.owners[AppOwnerRole.Security]}
          type={AppOwnerRole.Security}
        />
      </OwnerSection>
      <OwnerSection
        onEdit={() => {
          onEdit();
          updateAppOwnerEditRequest('role', AppOwnerRole.Watcher);
        }}
        title='Watchers'
        appId={app.appId}
        editable={resolveEditable(app.owners[AppOwnerRole.Watcher])}>
        <OwnersList
          onAddOwners={onEdit}
          owners={app.owners[AppOwnerRole.Watcher]}
          type={AppOwnerRole.Watcher}
        />
      </OwnerSection>
      <OwnerSection
        onEdit={() => {
          onEdit();
          updateAppOwnerEditRequest('role', AppOwnerRole.Business);
        }}
        title='Business Owners'
        appId={app.appId}
        editable={resolveEditable(app.owners[AppOwnerRole.Business])}>
        <OwnersList
          onAddOwners={onEdit}
          owners={app.owners[AppOwnerRole.Business]}
          type={AppOwnerRole.Business}
        />
      </OwnerSection>
      <OwnerSection
        onEdit={() => {
          onEdit();
          updateAppOwnerEditRequest('role', AppOwnerRole.Dev);
        }}
        title='Dev Owners'
        appId={app.appId}
        editable={resolveEditable(app.owners[AppOwnerRole.Dev])}>
        <OwnersList
          onAddOwners={onEdit}
          owners={app.owners[AppOwnerRole.Dev]}
          type={AppOwnerRole.Dev}
        />
      </OwnerSection>
      {app.moreOwners.length > 0 && (
        <div className={classes.more}>
          <Tooltip
            title={<OwnersTooltip owners={app.moreOwners} />}
            arrow
            placement='top'>
            <Typography color='primary' variant='body1' component='span'>
              {app.moreOwners.length} more
            </Typography>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

const resolveEditable = (owners?: Owner[]) => owners && owners.length > 0;

const useStyles = makeStyles()((theme: Theme) => ({
  personas: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, auto)',
    gap: theme.spacing(2),
  },
  more: {
    lineHeight: '1.5rem',
    '& > *': {
      verticalAlign: 'middle',
    },
  },
}));

export default Personas;
